import type { FeatureFlags } from "./types";

/**
 * Parse the Fitness Machine Feature characteristic (0x2ACC).
 *
 * Layout (spec §4.3): two consecutive 32-bit LE fields. The first is the
 * Fitness Machine Features bitmask (does the machine report cadence, power,
 * resistance, etc.). The second is the Target Setting Features bitmask
 * (can the machine accept resistance/power targets via the control point?).
 *
 * Defensive: short payloads are tolerated — missing fields default to 0.
 */
export function parseFeature(view: DataView): FeatureFlags {
  const fitness = view.byteLength >= 4 ? view.getUint32(0, true) : 0;
  const targets = view.byteLength >= 8 ? view.getUint32(4, true) : 0;
  const raw = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
  return { fitness, targets, raw: new Uint8Array(raw) };
}

export const EMPTY_FEATURES: FeatureFlags = Object.freeze({
  fitness: 0,
  targets: 0,
  raw: new Uint8Array(),
}) as FeatureFlags;

// Selected feature bits from spec §4.3.1.1 (Fitness Machine Features). Not all
// listed — extend as parsers grow. Names match the spec's section text so a
// grep against the SIG document succeeds.
export const FEATURE_AVERAGE_SPEED = 1 << 0;
export const FEATURE_CADENCE = 1 << 1;
export const FEATURE_TOTAL_DISTANCE = 1 << 2;
export const FEATURE_INCLINATION = 1 << 3;
export const FEATURE_ELEVATION_GAIN = 1 << 4;
export const FEATURE_PACE = 1 << 5;
export const FEATURE_STEP_COUNT = 1 << 6;
export const FEATURE_RESISTANCE_LEVEL = 1 << 7;
export const FEATURE_STRIDE_COUNT = 1 << 8;
export const FEATURE_EXPENDED_ENERGY = 1 << 9;
export const FEATURE_HEART_RATE = 1 << 10;
export const FEATURE_METABOLIC_EQUIVALENT = 1 << 11;
export const FEATURE_ELAPSED_TIME = 1 << 12;
export const FEATURE_REMAINING_TIME = 1 << 13;
export const FEATURE_POWER_MEASUREMENT = 1 << 14;
export const FEATURE_FORCE_ON_BELT = 1 << 15;
export const FEATURE_USER_DATA_RETENTION = 1 << 16;
